import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Router from 'next/router';
import TagManager from 'react-gtm-module';

const APP_NAME = 'startup-poker';
const APP_SURFACE = 'tools';

/**
 * True when the current browser context is driven by automation
 * (Playwright/WebDriver). Automated/test traffic must not be initialized into
 * GTM — each environment's container forwards to its GA4 stream, and prod
 * (GTM-NR75QK) feeds the prod stream, so bot hits would pollute it as if they
 * were organic visitors. Skipping init keeps automation out of GA4 entirely
 * regardless of which environment the harness runs against. Mirrors the proven
 * plinkromatic pattern (Kromatic-Innovation/plinkromatic#476 Problem 1).
 */
export function isAutomatedBrowser(
    nav: Pick<Navigator, 'webdriver'> | undefined = typeof navigator !== 'undefined' ? navigator : undefined
): boolean {
    return nav?.webdriver === true;
}

const PII_KEYS = new Set([
    'email',
    'name',
    'first_name',
    'last_name',
    'message',
    'company',
]);

/**
 * Push GA4's arguments-object gtag('set', { user_id }) form directly to the
 * dataLayer. Under react-gtm-module the container's `set_user_id` Custom-HTML
 * tag does NOT fire on the games, so the event-form
 * `{ event: 'set_user_id', user_id }` push is inert (live /collect probe
 * 2026-06-30: event-form attached no &uid; this arguments-object form DID).
 * GA4 honors the arguments-object form natively. Must be a REAL function (not
 * an arrow) so `arguments` is valid; GA4 ignores the plain ['set', {…}] array
 * form. The auid is a salted-HMAC pseudonym — never raw id/email/name.
 * (kromatic-global-issues#153 / #144)
 */
function pushGtagUserId(auid: string) {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    const gtag: (...args: unknown[]) => void = function gtag() {
        (w.dataLayer as unknown[]).push(arguments);
    };
    gtag('set', { user_id: auid });
}

const AnalyticsContext = createContext<AnalyticsState>({} as AnalyticsState);

export const useAnalytics = () => useContext(AnalyticsContext);

export function withAnalytics(WrappedComponent: any) {
    return function AnalyticsWrappedComponent(props: any) {
        return (
            <AnalyticsContext.Consumer>
                {state => <WrappedComponent {...props} analytics={state} />}
            </AnalyticsContext.Consumer>
        );
    };
}

type AnyRecord = Record<string, any>;

type GaIds = {
    ga_client_id?: string;
    ga_session_id?: string;
};

function safeDecode(value: string) {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function parseCookieString(cookieString: string): Record<string, string> {
    return cookieString
        .split(';')
        .map(cookie => cookie.trim())
        .filter(Boolean)
        .reduce<Record<string, string>>((acc, cookie) => {
            const separatorIndex = cookie.indexOf('=');
            if (separatorIndex === -1) {
                return acc;
            }

            const key = cookie.slice(0, separatorIndex).trim();
            const value = cookie.slice(separatorIndex + 1).trim();
            if (!key) {
                return acc;
            }

            acc[key] = safeDecode(value);
            return acc;
        }, {});
}

function parseGaClientId(rawValue?: string): string | undefined {
    if (!rawValue) {
        return undefined;
    }

    const value = rawValue.trim();
    if (!value) {
        return undefined;
    }

    const parts = value.split('.');
    if (parts.length >= 4 && parts[2] && parts[3]) {
        return `${parts[2]}.${parts[3]}`;
    }

    const dottedPair = value.match(/\d+\.\d+/)?.[0];
    return dottedPair ?? value;
}

function parseGaSessionId(rawValue?: string): string | undefined {
    if (!rawValue) {
        return undefined;
    }

    const value = rawValue.trim();
    if (!value) {
        return undefined;
    }

    const firstNumericChunk = value
        .split('.')
        .find(chunk => /^\d{6,}$/.test(chunk));

    return firstNumericChunk;
}

function getGaIdsFromBrowserCookies(): GaIds {
    if (typeof document === 'undefined') {
        return {};
    }

    const cookies = parseCookieString(document.cookie || '');
    const gaCookieValue = cookies._ga;
    const gaSessionCookieValue = Object.entries(cookies).find(
        ([key]) => key.startsWith('_ga_') && key !== '_ga'
    )?.[1];

    const ga_client_id = parseGaClientId(gaCookieValue);
    const ga_session_id = parseGaSessionId(gaSessionCookieValue);

    return {
        ...(ga_client_id ? { ga_client_id } : {}),
        ...(ga_session_id ? { ga_session_id } : {}),
    };
}

function assertNoPiiPayload(payload: AnyRecord, parentPath = '') {
    for (const [key, value] of Object.entries(payload)) {
        const normalizedKey = key.toLowerCase();
        const path = parentPath ? `${parentPath}.${key}` : key;

        if (PII_KEYS.has(normalizedKey)) {
            throw new Error(`Analytics payload contains restricted PII field: ${path}`);
        }

        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (item && typeof item === 'object') {
                    assertNoPiiPayload(item as AnyRecord, `${path}[${index}]`);
                }
            });
            continue;
        }

        if (value && typeof value === 'object') {
            assertNoPiiPayload(value as AnyRecord, path);
        }
    }
}

function generateEventId() {
    if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    return `ga_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function detectAuthState() {
    if (typeof window === 'undefined') {
        return 'guest';
    }

    const localStorageKeys = [
        'kromatic-user-t',
        'ct.api.token',
        'sp.api.token',
        'iaa.api.email',
        'pl.api.email',
        'pm.api.email',
    ];

    for (const key of localStorageKeys) {
        if (window.localStorage.getItem(key)) {
            return 'member';
        }
    }

    return 'guest';
}

function buildCanonicalPayload(parameters?: AnyRecord): AnyRecord {
    const now = Date.now();
    const pagePath =
        parameters?.page_path ||
        (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/');
    const gaIds = getGaIdsFromBrowserCookies();

    return {
        ...parameters,
        event_id: parameters?.event_id || generateEventId(),
        occurred_at_ms: parameters?.occurred_at_ms || now,
        page_path: pagePath,
        app_name: parameters?.app_name || APP_NAME,
        app_surface: parameters?.app_surface || APP_SURFACE,
        auth_state: parameters?.auth_state || detectAuthState(),
        resource_type: parameters?.resource_type ?? null,
        resource_id: parameters?.resource_id ?? null,
        source: parameters?.source ?? null,
        ga_client_id: parameters?.ga_client_id || gaIds.ga_client_id || null,
        ga_session_id: parameters?.ga_session_id || gaIds.ga_session_id || null,
    };
}

export function AnalyticsProvider({ children }: AnalyticsProps) {
    const [, setDebouncedEvents] = useState<any>({});
    const pathnameRef = useRef('');

    useEffect(() => {
        // Initialize Google Tag Manager with this environment's container, but
        // never for automated/test browsers — those would pollute the GA4 stream
        // with bot traffic indistinguishable from real visitors (plinkromatic#476
        // Problem 1, mirrored here).
        if (!process.env.NEXT_PUBLIC_GTM_ID || isAutomatedBrowser()) {
            return;
        }
        // Re-push set_user_id BEFORE GTM bootstrap on every authed load
        // (RSB#735 pattern). The GTM Google tag fires once on "All Pages" at
        // bootstrap and reads the user_id config param THEN. identify() runs
        // post-mount (after the /user/info/ fetch), so at bootstrap the dataLayer
        // has no user_id and nothing re-fires the tag. Persisting the auid
        // (Api.tsx) + re-pushing here lands user_id ahead of the All-Pages fire.
        // auid is a salted-HMAC pseudonym — never PII.
        if (typeof window !== 'undefined') {
            const auid = window.localStorage.getItem('kromatic-user-auid');
            const token = window.localStorage.getItem('kromatic-user-t');
            if (auid && token) {
                const w = window as any;
                w.dataLayer = w.dataLayer || [];
                w.dataLayer.push({ event: 'set_user_id', user_id: auid });
                // Also push the arguments-object gtag form GA4 honors directly:
                // the event-form above is inert under react-gtm-module (#153).
                pushGtagUserId(auid);
            }
        }
        TagManager.initialize({
            gtmId: process.env.NEXT_PUBLIC_GTM_ID,
        });
    }, []);

    const pushIdentity = useCallback((auid: string | undefined) => {
        // Automation/test traffic must never set a GA4 user_id (mirrors the
        // #476 init guard). Only push when GTM is the active sink.
        //
        // The NEXT_PUBLIC_GTM_ID guard is intentional, not a bug: user_id is a
        // GTM/GA4 concept only. The custom-analytics sink
        // (NEXT_PUBLIC_ANALYTICS_ACCOUNT/_URL) does NOT carry user_id by design,
        // so when GTM is unset there is no sink to identify into and this no-op
        // is correct.
        if (isAutomatedBrowser() || !process.env.NEXT_PUBLIC_GTM_ID) {
            return;
        }
        // GTM-native object form. The gtag(['set', …]) array form is ignored by
        // GTM, so we push { event: 'set_user_id', user_id } directly. The auid is
        // a salted-HMAC pseudonym (orca-back#1102) — never a raw id/email/name.
        (window as any)[TagManager.dataLayer.name]?.push({ event: 'set_user_id', user_id: auid });
        // Also push the arguments-object gtag form GA4 honors directly (#153).
        // The event-form above is inert under react-gtm-module on the games.
        // Only stamp a real auid — never the clearIdentity undefined (PII/blank
        // guard), which GA4 would otherwise read as an empty user_id.
        if (auid) {
            pushGtagUserId(auid);
        }
    }, []);

    const identify = useCallback(
        (auid: string | null | undefined) => {
            const normalized = typeof auid === 'string' && auid.trim() ? auid : undefined;
            pushIdentity(normalized);
        },
        [pushIdentity]
    );

    const clearIdentity = useCallback(() => {
        pushIdentity(undefined);
    }, [pushIdentity]);

    const trackEvent = useCallback(async (name: string, parameters?: any, debounce?: boolean | string) => {
        // Check debounce
        if (debounce) {
            // Get debounce id
            const parameterKeys = parameters ? Object.keys(parameters).join('-') : '';
            const id = `${name}-${typeof debounce === 'string' ? debounce : parameterKeys}`;

            // Clear previous and add new timeout
            setDebouncedEvents((prevDebouncedEvents: any) => {
                const newDebouncedEvents = { ...prevDebouncedEvents };
                clearTimeout(newDebouncedEvents[id]);
                newDebouncedEvents[id] = setTimeout(trackEvent, 1000, name, parameters);
                return newDebouncedEvents;
            });
            return;
        }

        // Track with custom analytics
        if (process.env.NEXT_PUBLIC_ANALYTICS_ACCOUNT && process.env.NEXT_PUBLIC_ANALYTICS_URL) {
            return fetch(process.env.NEXT_PUBLIC_ANALYTICS_URL, {
                body: JSON.stringify({
                    account: process.env.NEXT_PUBLIC_ANALYTICS_ACCOUNT,
                    attributes: parameters,
                    group: name,
                }),
                method: 'post',
            });
        }

        // Track with GTM
        if (process.env.NEXT_PUBLIC_GTM_ID) {
            return (window as any)[TagManager.dataLayer.name]?.push({
                event: name,
                ...parameters,
            });
        }
    }, []);

    const trackActionEvent = useCallback(
        async (name: string, parameters?: AnyRecord, debounce?: boolean | string) => {
            const normalizedPayload = buildCanonicalPayload(parameters);
            assertNoPiiPayload(normalizedPayload);
            return trackEvent(name, normalizedPayload, debounce);
        },
        [trackEvent]
    );

    const trackLeadEvent = useCallback(
        async (name: string, parameters?: AnyRecord, debounce?: boolean | string) => {
            const normalizedPayload = buildCanonicalPayload(parameters);
            assertNoPiiPayload(normalizedPayload);
            return trackEvent(name, normalizedPayload, debounce);
        },
        [trackEvent]
    );

    const trackPageview = useCallback(
        (pagePath?: string) => {
            const resolvedPath =
                pagePath ||
                (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/');

            return trackEvent('Pageview', {
                pagePath: resolvedPath,
            });
        },
        [trackEvent]
    );

    useEffect(() => {
        const handleRouteChange = (url?: string) => {
            const nextPath =
                url ||
                (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/');

            if (pathnameRef.current === nextPath) {
                return;
            }

            pathnameRef.current = nextPath;
            trackPageview(nextPath);
        };

        handleRouteChange();

        Router.events.on('routeChangeComplete', handleRouteChange);

        return () => {
            Router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [trackPageview]);

    const state = useMemo(
        () => ({
            enabled: !!(
                process.env.NEXT_PUBLIC_GTM_ID ||
                (process.env.NEXT_PUBLIC_ANALYTICS_ACCOUNT && process.env.NEXT_PUBLIC_ANALYTICS_URL)
            ),
            identify,
            clearIdentity,
            trackActionEvent,
            trackEvent,
            trackLeadEvent,
            trackPageview,
        }),
        [identify, clearIdentity, trackActionEvent, trackEvent, trackLeadEvent, trackPageview]
    );

    return <AnalyticsContext.Provider value={state}>{children}</AnalyticsContext.Provider>;
}
